import type { PolygonWithHoles, Ring, Vec2 } from '../../geo/types';
import { pointInPolygon, ringBounds } from '../../geo/polygon';
import type { Bank } from './banks';

export type Span = readonly [number, number];

// A deck's prism rises to the quay's top, so the revetment's own timbers stop this far short of it.
export const DECK_CLEARANCE_M = 0.1;

// Probe lines through the band a revetment's timbers take in front of the bank.
const PROBE_SHARES = [0.05, 0.5, 0.95];

function crossing(p: Vec2, q: Vec2, c: Vec2, d: Vec2): number | null {
  const [rx, ry, sx, sy] = [q[0] - p[0], q[1] - p[1], d[0] - c[0], d[1] - c[1]];
  const denominator = rx * sy - ry * sx;
  if (Math.abs(denominator) < 1e-12) return null;
  const [cx, cy] = [c[0] - p[0], c[1] - p[1]];
  const t = (cx * sy - cy * sx) / denominator;
  const s = (cx * ry - cy * rx) / denominator;
  return s >= 0 && s <= 1 && t > 0 && t < 1 ? t : null;
}

function ringsOf(deck: PolygonWithHoles): readonly Ring[] {
  return [deck.outer, ...deck.holes];
}

/** The stretches of the line p→q, as shares of it, that lie inside the deck. */
function insideShares(p: Vec2, q: Vec2, deck: PolygonWithHoles): Span[] {
  const cuts = ringsOf(deck)
    .flatMap((ring) => ring.map((c, i) => crossing(p, q, c, ring[(i + 1) % ring.length]!)))
    .filter((t): t is number => t !== null);
  const ts = [0, ...cuts.sort((x, y) => x - y), 1];
  return ts.slice(1).flatMap((t1, i): Span[] => {
    const t0 = ts[i]!;
    const mid = (t0 + t1) / 2;
    const at: Vec2 = [p[0] + (q[0] - p[0]) * mid, p[1] + (q[1] - p[1]) * mid];
    return t1 > t0 && pointInPolygon(at, deck) ? [[t0, t1]] : [];
  });
}

function merged(spans: readonly Span[]): Span[] {
  const sorted = [...spans].sort((x, y) => x[0] - y[0]);
  return sorted.reduce<Span[]>((out, span) => {
    const last = out[out.length - 1];
    if (last && span[0] <= last[1])
      return [...out.slice(0, -1), [last[0], Math.max(last[1], span[1])]];
    return [...out, span];
  }, []);
}

function nearBank(bank: Bank, deck: PolygonWithHoles, reach: number): boolean {
  const box = ringBounds(deck.outer);
  const margin = reach + DECK_CLEARANCE_M;
  return (
    box.minX <= Math.max(bank.a[0], bank.b[0]) + margin &&
    box.maxX >= Math.min(bank.a[0], bank.b[0]) - margin &&
    box.minY <= Math.max(bank.a[1], bank.b[1]) + margin &&
    box.maxY >= Math.min(bank.a[1], bank.b[1]) - margin
  );
}

/**
 * The stretches of a bank, in metres along it, where a bridge deck lies over the band `reach` wide in
 * front of it, each widened by the clearance and kept within the bank.
 */
export function coveredSpans(
  bank: Bank,
  decks: readonly PolygonWithHoles[],
  reach: number
): Span[] {
  const [dx, dy] = bank.direction;
  const toward: Vec2 = [dy, -dx];
  const offset = (p: Vec2, d: number): Vec2 => [p[0] + toward[0] * d, p[1] + toward[1] * d];
  const spans = decks
    .filter((deck) => nearBank(bank, deck, reach))
    .flatMap((deck) =>
      PROBE_SHARES.flatMap((share) =>
        insideShares(offset(bank.a, reach * share), offset(bank.b, reach * share), deck)
      )
    )
    .map(([t0, t1]): Span => [
      Math.max(0, t0 * bank.length - DECK_CLEARANCE_M),
      Math.min(bank.length, t1 * bank.length + DECK_CLEARANCE_M),
    ]);
  return merged(spans);
}

/** What is left of [0, length] outside the covered spans, which come merged and in order. */
export function openSpans(length: number, covered: readonly Span[]): Span[] {
  const edges = [0, ...covered.flatMap(([from, to]) => [from, to]), length];
  const out: Span[] = [];
  for (let i = 0; i < edges.length; i += 2) {
    if (edges[i + 1]! > edges[i]!) out.push([edges[i]!, edges[i + 1]!]);
  }
  return out;
}
